/**
 * Announcement Bar Component
 * Adds decorative SVG arrows positioned adjacent to announcement bar text
 */

class AnnouncementBar {
  /**
   * @param {Document|HTMLElement} container - Container element to search within
   */
  constructor(container = document) {
    this.container = container
    this.svgArrow = `
      <svg width="13" height="9" viewBox="0 0 13 9" fill="none" xmlns="http://www.w3.org/2000/svg">
        <g opacity="0.5">
          <path d="M0 4.30862H11.8606" stroke="currentColor" stroke-width="0.5" stroke-linejoin="round"/>
          <path d="M8.38672 0.417908L11.8605 4.30862L8.38672 8.19933" stroke="currentColor" stroke-width="0.5" stroke-linejoin="round"/>
          <path d="M6.31372 1.12674L11.8606 4.30863L6.31372 7.49364" stroke="currentColor" stroke-width="0.5" stroke-linejoin="round"/>
          <path d="M4.302 2.74423L11.8606 4.30864L4.302 5.87616" stroke="currentColor" stroke-width="0.5" stroke-linejoin="round"/>
        </g>
      </svg>
    `
    this.init()
  }

  /**
   * Initialize the component
   */
  init() {
    this.addArrowsToAllTexts()
    this.bindEvents()
  }

  /**
   * Bind event listeners for dynamic content changes
   */
  bindEvents() {
    const observer = new MutationObserver((mutations) => {
      let shouldRefresh = false
      
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              const element = node
              if (element.classList && element.classList.contains('announcement-bar__carousel')) {
                shouldRefresh = true
              } else if (element.querySelector && element.querySelector('.announcement-bar__carousel')) {
                shouldRefresh = true
              }
            }
          })
        }
      })
      
      if (shouldRefresh) {
        this.refresh()
      }
    })
    
    if (this.container instanceof Element) {
      observer.observe(this.container, {
        childList: true,
        subtree: true
      })
    }
    
    this.observer = observer
  }

  /**
   * Add arrows to all text elements in announcement bar carousels
   */
  addArrowsToAllTexts() {
    const announcementCarousels = this.container.querySelectorAll('.announcement-bar__carousel')
    
    announcementCarousels.forEach((carousel) => {
      const textElements = carousel.querySelectorAll('p')
      
      textElements.forEach((textElement) => {
        // Skip if already has arrows
        if (textElement.querySelector('.announcement-bar-arrow')) {
        return
      }

        // Make text element position relative to contain arrows
        textElement.style.position = 'relative'
        textElement.style.display = 'inline-block'

      const leftArrow = document.createElement('div')
      leftArrow.className = 'announcement-bar-arrow announcement-bar-arrow--left'
      leftArrow.innerHTML = this.svgArrow
      
      const rightArrow = document.createElement('div')
      rightArrow.className = 'announcement-bar-arrow announcement-bar-arrow--right'
      rightArrow.innerHTML = this.svgArrow
      
        textElement.appendChild(leftArrow)
        textElement.appendChild(rightArrow)
        
        // Position arrows after a brief delay to allow text measurement
    requestAnimationFrame(() => {
          this.positionArrowsForText(textElement, leftArrow, rightArrow)
        })
      })
    })
  }

  /**
   * Position arrows relative to their text element
   */
  positionArrowsForText(textElement, leftArrow, rightArrow) {
    const textRect = textElement.getBoundingClientRect()
    
    if (textRect.width === 0) {
      return false
    }
    
    const arrowWidth = 25
    
    leftArrow.style.left = `-${arrowWidth}px`
    leftArrow.style.top = '50%'
    leftArrow.style.transform = 'translateY(-50%)'
    
    rightArrow.style.right = `-${arrowWidth}px`
    rightArrow.style.top = '50%'
    rightArrow.style.transformOrigin = 'center center'
    rightArrow.style.transform = 'translateY(-50%) rotate(180deg)'
    
    return true
  }

  /**
   * Remove all arrow elements
   */
  removeArrows() {
    const arrows = this.container.querySelectorAll('.announcement-bar-arrow')
    arrows.forEach(arrow => {
      if (arrow.parentNode) {
        arrow.parentNode.removeChild(arrow)
      }
    })
  }

  /**
   * Refresh the component
   */
  refresh() {
    this.removeArrows()
    
    // Small delay to allow for DOM updates
    setTimeout(() => {
      this.addArrowsToAllTexts()
    }, 50)
  }

  /**
   * Destroy the component and clean up
   */
  destroy() {
    this.removeArrows()
    
    if (this.observer) {
      this.observer.disconnect()
    }
  }
}

// Initialize the component for the global managers system
export default AnnouncementBar 